import {afterRender, HostListener, Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {BehaviorSubject, debounceTime, distinctUntilChanged, fromEvent, Observable} from "rxjs";
import {WordpressService} from "./wordpress.service";
import {DOCUMENT, isPlatformBrowser} from "@angular/common";
import {WP_REST_API_Language} from "../models/Wordpress/WP_REST_API_Language";
import {LanguageService} from "./language.service";
import {NgcCookieConsentConfig, NgcCookieConsentService} from "ngx-cookieconsent";
import dayjs from "dayjs";

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  template : 'home'|'page' = 'page'
  template$  = new BehaviorSubject<'home'|'page'>('page');
  slug$ = new BehaviorSubject<string>('');
  resize$: Observable<Event> = new Observable<Event>();
  onResize$ = new BehaviorSubject<boolean>(true);
  viewportWidth = 0;
  viewportHeight = 0;
  mobileBreakpoint = 768;
  tabletBreakpoint = 992;
  desktopBreakpoint = 1200;
  public languages: WP_REST_API_Language[];
  public currentLanguage: WP_REST_API_Language | undefined;
  constructor(private wp : WordpressService, @Inject(DOCUMENT) private doc: Document,@Inject(PLATFORM_ID) private platformId: Object, public lang: LanguageService, public cookieContentService:NgcCookieConsentService) {
    this.initTheme();
    afterRender(()=>{
      this.updateThemeColor();
      this.viewportWidth = window.innerWidth;
      this.viewportHeight = window.innerHeight;
      this.resize$ = fromEvent(window, 'resize');
      this.resize$.pipe(
        debounceTime(1000),
        distinctUntilChanged()
      ).subscribe(()=>{
        this.onResize();
      });
    })
  }

  setCurrentTemplate(template : 'home'|'page'){
    this.template = template;
    this.template$.next(this.template);
  }

  initTheme(){
    this.wp.getThemeColors().then((theme) => {
      this.doc.body.classList.add(theme);
      this.updateFavicon(theme);
    })
  }

  updateThemeColor() {
    const themeColorMeta = this.doc.querySelector('meta[name="theme-color"]');
    if (themeColorMeta) {
      const color = getComputedStyle(this.doc.body).getPropertyValue('--text-brand');
      themeColorMeta.setAttribute('content', color);
    }
  }

  updateFavicon(theme:string) {
    const favicon = this.doc.querySelector('link[rel="icon"]');
    if (favicon) {
      let href =  `/assets/icons/favicon-${theme}.png`;
      favicon.setAttribute('href', href);
    }
  }

  onResize() {
    this.viewportWidth = window.innerWidth;
    this.viewportHeight = window.innerHeight;
    this.onResize$.next(true);
  }

  isMobile(){
    return this.viewportWidth < this.mobileBreakpoint;
  }

  isTablet(){
    return !this.isMobile() && this.viewportWidth < this.tabletBreakpoint;
  }
  isDesktop(){
    return !this.isMobile() && !this.isTablet();
  }

  setLanguageList(type:'home'|'page'|'fiche', id?:string|number, slug?:string): Promise<void>{
    return new Promise((resolve, reject)=>{
      this.wp.getLanguages(type,id,slug).then((languages: any)=>{
        this.languages = (Object.values(languages) as unknown as WP_REST_API_Language[]) ?? [];
        this.currentLanguage = this.languages.find((language)=>
          language.slug === this.lang.current
        )
        resolve()
      });
    });
  }

  initCookieConsent(){
    if (isPlatformBrowser(this.platformId)) {
      const contentEN = {
        message: 'This website uses cookies to ensure you get the best experience on our website.',
        dismiss: 'Got it!',
        deny: 'Refuse cookies',
        link: 'Learn more',
        href: '/privacy-policy',
        policy: 'Cookie Policy',
        target: '_self',
        header: 'Cookies used on the website!',
        allow: 'Allow cookies'
      }
      const contentFR = {
        message: 'Ce site utilise des cookies pour vous garantir la meilleure expérience sur notre site.',
        dismiss: 'J\'ai compris!',
        deny: 'Refuser les cookies',
        link: 'En savoir plus',
        href: '/donnees-personnelles',
        policy: 'Politique de cookies',
        target: '_self',
        header: 'Cookies utilisés sur le site!',
        allow: 'Autoriser les cookies'
      }
      const domain = this.doc.location.hostname;
      const cookieConfig: NgcCookieConsentConfig = {
        cookie: {
          domain: domain
        },
        palette: {
          popup: {
            background: '#000'
          },
          button: {
            background: window.getComputedStyle(this.doc.body).getPropertyValue('--surface-brand'),
          }
        },
        position: "bottom-left",
        theme: 'edgeless',
        type: 'opt-out',
        content: this.lang.current === 'en' ? contentEN : contentFR
      };
      this.cookieContentService.destroy();
      this.cookieContentService.init(cookieConfig);
    }
  }
}
